import { useState, useEffect } from "react";
import { User, CreditCard, Settings2, RotateCcw, HelpCircle } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useProfile, useUpdateProfile } from "@/hooks/useProfile";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { PlanSection } from "@/components/settings/PlanSection";
import { AvatarUpload } from "@/components/settings/AvatarUpload";
import { SupportSection } from "@/components/settings/SupportSection";
import { Separator } from "@/components/ui/separator";
import { useResetTour } from "@/hooks/useOnboarding";
import { useOnboardingContext } from "@/components/onboarding/OnboardingProvider";

const paymentMethodOptions = [
  { value: 'pix', label: 'PIX', icon: '💰' },
  { value: 'bank_transfer', label: 'Transferência Bancária', icon: '🏦' },
  { value: 'cash', label: 'Dinheiro', icon: '💵' },
  { value: 'boleto', label: 'Boleto', icon: '📄' },
  { value: 'credit_card', label: 'Cartão de Crédito', icon: '💳' },
  { value: 'paypal', label: 'PayPal', icon: '🌐' },
  { value: 'other', label: 'Outro', icon: '📦' },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const { data: profile, isLoading } = useProfile();
  const updateProfile = useUpdateProfile();
  const resetTour = useResetTour();
  const { restartTour } = useOnboardingContext();
  const { toast } = useToast();

  const [fullName, setFullName] = useState("");
  const [hourlyRate, setHourlyRate] = useState("");
  const [monthlyGoal, setMonthlyGoal] = useState("");
  const [currency, setCurrency] = useState("BRL");
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState("pix");

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || "");
      setHourlyRate(profile.hourly_rate?.toString() || "");
      setMonthlyGoal(profile.monthly_goal?.toString() || "");
      setCurrency(profile.currency || "BRL");
      setDefaultPaymentMethod((profile as any).default_payment_method || "pix");
    }
  }, [profile]);

  const handleSaveProfile = async () => {
    try {
      await updateProfile.mutateAsync({
        full_name: fullName.trim() || null,
      });
      toast({
        title: "Sucesso!",
        description: "Perfil atualizado com sucesso.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar o perfil.",
      });
    }
  };

  const handleSavePreferences = async () => {
    try {
      await updateProfile.mutateAsync({
        hourly_rate: hourlyRate ? parseFloat(hourlyRate) : null,
        monthly_goal: monthlyGoal ? parseFloat(monthlyGoal) : null,
        currency: currency,
        default_payment_method: defaultPaymentMethod,
      } as any);
      toast({
        title: "Sucesso!",
        description: "Preferências salvas com sucesso.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível salvar as preferências.",
      });
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout title="Configurações">
        <div className="max-w-3xl mx-auto space-y-8">
          <Skeleton className="h-48" />
          <Skeleton className="h-64" />
          <Skeleton className="h-48" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Configurações">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Profile Section */}
        <Card className="shadow-card overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-primary/10 to-primary/5 pb-16">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              <CardTitle>Perfil</CardTitle>
            </div>
            <CardDescription>
              Suas informações pessoais
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {/* Avatar Upload */}
            <AvatarUpload />

            {/* Form */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Nome completo</Label>
                <Input 
                  id="name" 
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Seu nome completo"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email" 
                  type="email" 
                  value={user?.email || ""}
                  disabled
                  className="bg-muted"
                />
              </div>
            </div>

            <Button 
              onClick={handleSaveProfile}
              disabled={updateProfile.isPending}
              className="mt-6"
            >
              {updateProfile.isPending ? "Salvando..." : "Salvar Perfil"}
            </Button>
          </CardContent>
        </Card>

        <Separator />

        {/* Plan Section */}
        <Card className="shadow-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" />
              <CardTitle>Plano</CardTitle>
            </div>
            <CardDescription>
              Gerencie sua assinatura e faturamento
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PlanSection />
          </CardContent>
        </Card>

        <Separator />

        {/* Preferences Section */}
        <Card className="shadow-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-primary" />
              <CardTitle>Preferências</CardTitle>
            </div>
            <CardDescription>
              Configure suas preferências de trabalho e pagamentos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-2">
                <Label htmlFor="hourlyRate">Valor hora padrão</Label>
                <Input
                  id="hourlyRate"
                  type="number"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  placeholder="100.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Moeda</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BRL">Real (R$)</SelectItem>
                    <SelectItem value="USD">Dólar (US$)</SelectItem>
                    <SelectItem value="EUR">Euro (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="monthlyGoal">Meta mensal</Label>
                <Input
                  id="monthlyGoal"
                  type="number"
                  value={monthlyGoal}
                  onChange={(e) => setMonthlyGoal(e.target.value)}
                  placeholder="10000.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="defaultPaymentMethod">Forma de pagamento padrão</Label>
                <Select value={defaultPaymentMethod} onValueChange={setDefaultPaymentMethod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethodOptions.map((method) => (
                      <SelectItem key={method.value} value={method.value}>
                        <span className="flex items-center gap-2">
                          <span>{method.icon}</span>
                          <span>{method.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button 
              onClick={handleSavePreferences}
              disabled={updateProfile.isPending}
            >
              {updateProfile.isPending ? "Salvando..." : "Salvar Preferências"}
            </Button>
          </CardContent>
        </Card>

        <Separator />

        {/* Tour Section */}
        <Card className="shadow-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-primary" />
              <CardTitle>Apresentação do Sistema</CardTitle>
            </div>
            <CardDescription>
              Reveja o tour guiado das funcionalidades
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Caso queira rever a apresentação das principais ferramentas do FreelaDash, 
              clique no botão abaixo para reiniciar o tour guiado.
            </p>
            <Button 
              variant="outline"
              onClick={async () => {
                await resetTour.mutateAsync();
                restartTour();
                toast({
                  title: "Tour reiniciado!",
                  description: "Siga as instruções na tela para conhecer as ferramentas.",
                });
              }}
              disabled={resetTour.isPending}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              {resetTour.isPending ? "Reiniciando..." : "Rever apresentação do sistema"}
            </Button>
          </CardContent>
        </Card>

        <Separator />

        {/* Support Section */}
        <Card className="shadow-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-primary" />
              <CardTitle>Suporte</CardTitle>
            </div>
            <CardDescription>
              Entre em contato com nossa equipe
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SupportSection />
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
